import * as Sentry from '@sentry/vue'
import { sentryCountMetric } from './sentryMetrics.ts'

export const AUTO_RELOAD_BUDGET_WINDOW_MS = 60_000
export const AUTO_RELOAD_BUDGET_MAX = 10
const AUTO_RELOAD_BUDGET_KEY = 'auto_reload_budget_timestamps'

export type AutoReloadBudgetResult = { ok: true } | { ok: false; reason: 'budget_exhausted' }

function readRecentTimestamps(now: number): number[] {
  try {
    const raw = sessionStorage.getItem(AUTO_RELOAD_BUDGET_KEY)
    if (!raw) return []

    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []

    return parsed.filter(
      (value): value is number =>
        typeof value === 'number' &&
        Number.isFinite(value) &&
        value <= now &&
        now - value <= AUTO_RELOAD_BUDGET_WINDOW_MS,
    )
  } catch {
    return []
  }
}

function writeTimestamps(timestamps: number[]): void {
  try {
    sessionStorage.setItem(AUTO_RELOAD_BUDGET_KEY, JSON.stringify(timestamps))
  } catch {
    // Ignore storage failures; in-memory reload still proceeds for this page.
  }
}

function reportBudgetExhausted(reason: string, timestamps: number[]): void {
  sentryCountMetric('reload.budget_exhausted')
  Sentry.addBreadcrumb({
    category: 'reload.budget',
    message: 'Auto-reload budget exhausted',
    level: 'warning',
    data: {
      reason,
      recentReloadCount: timestamps.length,
      windowMs: AUTO_RELOAD_BUDGET_WINDOW_MS,
      maxReloads: AUTO_RELOAD_BUDGET_MAX,
    },
  })
}

export function tryConsumeAutoReload(reason: string): AutoReloadBudgetResult {
  const now = Date.now()
  const timestamps = readRecentTimestamps(now)

  if (timestamps.length >= AUTO_RELOAD_BUDGET_MAX) {
    reportBudgetExhausted(reason, timestamps)
    return { ok: false, reason: 'budget_exhausted' }
  }

  writeTimestamps([...timestamps, now])
  return { ok: true }
}

/** Test helper — clears persisted budget state. */
export function resetAutoReloadBudgetForTests(): void {
  try {
    sessionStorage.removeItem(AUTO_RELOAD_BUDGET_KEY)
  } catch {
    // Ignore
  }
}
